import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const statusLabel = {
  NUEVO: 'Nuevo', ASIGNADO: 'Asignado', EN_PROCESO: 'En proceso',
  EN_ESPERA: 'En espera', COMPLETADO: 'Completado', CANCELADO: 'Cancelado'
};
const statusColor = {
  NUEVO:      { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' },
  ASIGNADO:   { bg: '#fffbeb', text: '#d97706', border: '#fde68a' },
  EN_PROCESO: { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' },
  EN_ESPERA:  { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
  COMPLETADO: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  CANCELADO:  { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
};
const priorityColor = {
  BAJA:    { bg: '#f1f5f9', text: '#64748b' },
  MEDIA:   { bg: '#eff6ff', text: '#2563eb' },
  ALTA:    { bg: '#fff7ed', text: '#ea580c' },
  CRITICA: { bg: '#fef2f2', text: '#dc2626' },
};

const ROLES_GLOBALES = ['ADMIN', 'DIRECTOR', 'GERENTE'];

export default function SolicitudDetallePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [solicitud, setSolicitud] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [image, setImage] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [newAreaId, setNewAreaId] = useState('');
  const [wrongAreaComment, setWrongAreaComment] = useState('');
  const [showWrongAreaForm, setShowWrongAreaForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchSolicitud = async () => {
    const res = await api.get(`/requests/${id}`);
    setSolicitud(res.data);
    setNewStatus(res.data.status);
    setAssignedToId(res.data.assignedToId || '');
    setNewAreaId(res.data.areaId || '');
  };

  useEffect(() => {
    fetchSolicitud().catch(() => navigate('/solicitudes')).finally(() => setLoading(false));
    api.get('/areas').then(r => setAreas(r.data)).catch(() => {});
    if (user?.role !== 'OPERADOR') {
      api.get('/auth/users').then(res => {
        const todos = res.data.filter(u => u.active);
        if (user?.role === 'SUPERVISOR' && user?.areaId) {
          setUsuarios(todos.filter(u => Number(u.areaId) === Number(user.areaId) && u.role === 'OPERADOR'));
        } else {
          setUsuarios(todos);
        }
      }).catch(() => {});
    }
  }, [id]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() && !image) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('text', comment);
      if (image) formData.append('image', image);
      await api.post(`/requests/${id}/comments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setComment(''); setImage(null);
      fetchSolicitud();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al agregar comentario');
    } finally { setSubmitting(false); }
  };

  const handleStatusChange = async () => {
    if (newStatus === solicitud.status) return;
    const requiresComment = newStatus === 'EN_ESPERA' || newStatus === 'CANCELADO';
    if (requiresComment && !comment.trim()) {
      alert(`Debes agregar un comentario para cambiar el estatus a "${statusLabel[newStatus]}"`);
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/requests/${id}/status`, { status: newStatus, comment });
      setComment('');
      fetchSolicitud();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar estatus');
    } finally { setSubmitting(false); }
  };

  const handleAssign = async () => {
    if (!assignedToId || Number(assignedToId) === solicitud.assignedToId) return;
    setSubmitting(true);
    try {
      await api.patch(`/requests/${id}/assign`, { assignedToId: Number(assignedToId) });
      fetchSolicitud();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al asignar');
    } finally { setSubmitting(false); }
  };

  const handleChangeArea = async () => {
    if (!newAreaId || Number(newAreaId) === solicitud.areaId) return;
    setSubmitting(true);
    try {
      await api.patch(`/requests/${id}/area`, { areaId: Number(newAreaId) });
      fetchSolicitud();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar área');
    } finally { setSubmitting(false); }
  };

  const handleReportWrongArea = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/requests/${id}/report-wrong-area`, { comment: wrongAreaComment });
      setWrongAreaComment(''); setShowWrongAreaForm(false);
      alert('Área incorrecta reportada. Los gestores han sido notificados.');
      fetchSolicitud();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reportar');
    } finally { setSubmitting(false); }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;
  if (!solicitud) return null;

  const sc = statusColor[solicitud.status] || {};
  const pc = priorityColor[solicitud.priority] || {};
  const isGlobal = ROLES_GLOBALES.includes(user?.role);
  const isSupervisorOrOp = ['SUPERVISOR', 'OPERADOR'].includes(user?.role);

  // Determinar si los botones deben estar habilitados
  const asignarChanged = assignedToId && Number(assignedToId) !== solicitud.assignedToId;
  const statusChanged = newStatus && newStatus !== solicitud.status;
  const areaChanged = newAreaId && Number(newAreaId) !== solicitud.areaId;

  const btnStyle = (enabled) => ({
    border: 'none', borderRadius: 10, padding: '9px 16px',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    cursor: enabled && !submitting ? 'pointer' : 'not-allowed',
    opacity: enabled && !submitting ? 1 : 0.45,
    transition: 'opacity 0.2s'
  });

  return (
    <div style={{ maxWidth: 860, fontFamily: "'Segoe UI', sans-serif" }}>
      <button onClick={() => navigate('/solicitudes')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, padding: 0 }}>
        <ArrowLeft size={16} /> Volver a solicitudes
      </button>

      {/* Encabezado */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
          <div>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#2563eb', fontWeight: 700 }}>{solicitud.folio}</span>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1a2f6b', margin: '4px 0 6px' }}>{solicitud.description}</h1>
            <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>Solicitante: {solicitud.requesterName}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <span style={{ background: pc.bg, color: pc.text, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{solicitud.priority}</span>
            <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{statusLabel[solicitud.status]}</span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, fontSize: 13, color: '#64748b' }}>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Área: </span>{solicitud.area?.name}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Creado por: </span>{solicitud.createdBy?.name}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Asignado a: </span>{solicitud.assignedTo?.name || 'Sin asignar'}</div>
          <div><span style={{ fontWeight: 600, color: '#374151' }}>Fecha: </span>{new Date(solicitud.createdAt).toLocaleString('es-MX')}</div>
          {solicitud.closedAt && <div><span style={{ fontWeight: 600, color: '#374151' }}>Cerrado: </span>{new Date(solicitud.closedAt).toLocaleString('es-MX')}</div>}
        </div>
      </div>

      {/* Acciones */}
      {(isGlobal || user?.role === 'SUPERVISOR') && (
        <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Asignar */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Asignar responsable</label>
                <select value={assignedToId} onChange={e => setAssignedToId(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="">Sin asignar</option>
                  {usuarios.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select>
                {!asignarChanged && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>Selecciona un responsable diferente para habilitar</p>}
              </div>
              <button onClick={handleAssign} disabled={!asignarChanged || submitting}
                style={{ ...btnStyle(asignarChanged), background: asignarChanged && !submitting ? '#2563eb' : '#93c5fd', color: 'white', whiteSpace: 'nowrap' }}>
                Asignar
              </button>
            </div>

            {/* Cambiar estatus */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Cambiar estatus</label>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                  {Object.entries(statusLabel).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                </select>
                {!statusChanged && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>Selecciona un estatus diferente para habilitar</p>}
              </div>
              <button onClick={handleStatusChange} disabled={!statusChanged || submitting}
                style={{ ...btnStyle(statusChanged), background: statusChanged && !submitting ? '#16a34a' : '#86efac', color: 'white', whiteSpace: 'nowrap' }}>
                Cambiar
              </button>
            </div>

            {/* Cambiar área — solo roles globales */}
            {isGlobal && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>Cambiar área</label>
                  <select value={newAreaId} onChange={e => setNewAreaId(e.target.value)}
                    style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}>
                    {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  {!areaChanged && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>Selecciona un área diferente para habilitar</p>}
                </div>
                <button onClick={handleChangeArea} disabled={!areaChanged || submitting}
                  style={{ ...btnStyle(areaChanged), background: areaChanged && !submitting ? '#7c3aed' : '#c4b5fd', color: 'white', whiteSpace: 'nowrap' }}>
                  Cambiar
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reportar área incorrecta */}
      {isSupervisorOrOp && (
        <div style={{ background: '#fffbeb', borderRadius: 16, border: '1px solid #fde68a', padding: 16, marginBottom: 16 }}>
          {!showWrongAreaForm ? (
            <button onClick={() => setShowWrongAreaForm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#d97706', fontWeight: 600, fontSize: 14, fontFamily: 'inherit' }}>
              <AlertTriangle size={16} /> Reportar área incorrecta
            </button>
          ) : (
            <form onSubmit={handleReportWrongArea}>
              <p style={{ margin: '0 0 10px', fontWeight: 600, color: '#d97706', fontSize: 13 }}>
                <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                ¿Por qué crees que el área es incorrecta?
              </p>
              <textarea value={wrongAreaComment} onChange={e => setWrongAreaComment(e.target.value)} rows={2}
                placeholder="Describe el problema (opcional)"
                style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #fde68a', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', marginBottom: 10, resize: 'none' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={submitting}
                  style={{ background: '#d97706', color: 'white', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Enviando...' : 'Reportar'}
                </button>
                <button type="button" onClick={() => setShowWrongAreaForm(false)}
                  style={{ background: 'none', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#d97706', fontFamily: 'inherit' }}>
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Comentarios */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1a2f6b' }}>Comentarios</h2>
        <div style={{ marginBottom: 20 }}>
          {solicitud.comments?.length === 0 && <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin comentarios aún.</p>}
          {solicitud.comments?.map(c => (
            <div key={c.id} style={{ background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 10, borderLeft: '3px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: '#1a2f6b', fontSize: 13 }}>{c.user?.name}</span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{new Date(c.createdAt).toLocaleString('es-MX')}</span>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{c.text}</p>
              {c.imageUrl && (
                <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000'}${c.imageUrl}`}
                  alt="evidencia" style={{ marginTop: 10, borderRadius: 10, maxHeight: 200, objectFit: 'cover', maxWidth: '100%' }} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleComment}>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
            placeholder="Escribe un comentario..."
            style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: 12 }}
            onFocus={e => e.target.style.borderColor = '#2563eb'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <label style={{ fontSize: 13, color: '#2563eb', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
              📎 {image ? image.name : 'Adjuntar imagen'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => setImage(e.target.files[0])}
              />
            </label>
            {image && (
              <button type="button" onClick={() => setImage(null)}
                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>
                ✕ Quitar imagen
              </button>
            )}
            <button type="submit" disabled={submitting || (!comment.trim() && !image)}
              style={{ ...btnStyle(comment.trim() || image), background: (comment.trim() || image) && !submitting ? '#2563eb' : '#93c5fd', color: 'white' }}>
              {submitting ? 'Enviando...' : 'Agregar comentario'}
            </button>
          </div>
        </form>
      </div>

      {/* Bitácora */}
      <div style={{ background: 'white', borderRadius: 20, boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0', padding: 24 }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1a2f6b' }}>Bitácora de actividad</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {solicitud.auditLogs?.map(log => (
            <div key={log.id} style={{ display: 'flex', gap: 12, fontSize: 13 }}>
              <span style={{ color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap', marginTop: 2 }}>{new Date(log.createdAt).toLocaleString('es-MX')}</span>
              <span style={{ color: '#374151' }}>
                <span style={{ fontWeight: 700, color: '#1a2f6b' }}>{log.user?.name}</span>
                {' — '}{log.action}
                {log.details && <span style={{ color: '#64748b' }}> · {log.details}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}